import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import PreviewImage from '../PreviewImage';
import {
  getAppropriateBg,
  getAppropriateForeground,
} from '../_pages/_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import usePackagePoses from '../../hooks/usePackagePoses';
import usePricelist from '../../hooks/usePricelist';
import useImageOrientation, { ORIENTATION } from '../../hooks/useImageOrientation';
import { cameraLetterOf, poseNumberOf } from '../CustomerCart/cameraSort';
import { CapIcon, HeartIcon } from './icons';

const readFavorites = (key) => {
  if (!key) return new Set();
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_) {
    return new Set();
  }
};

/**
 * A small flanking portrait for the "Growing Up" header (last year / this year). The proof
 * is a transparent PNG cutout, so it composites onto a backdrop exactly like a gallery card
 * — the caller passes the same backdrop for both years, so the child's growth is the change,
 * not the background.
 */
function GrowthThumbnail({ thumb, tiltClassName, wrapperClassName }) {
  // NB: no Tailwind `hidden` here — Bootstrap (coexisting) defines `.hidden { display:none
  // !important }`, which a Tailwind responsive `block` cannot override. Size responsively
  // instead so the thumbnails stay visible on every width (~72px on phones, larger up).
  return (
    <div className={cn('shrink-0 w-[4.5rem] sm:w-24 md:w-28', wrapperClassName)}>
      {/* 8x10 (4:5) native portrait ratio, no cover-crop. The TILT lives here, on the image
          only — the grade label beneath stays upright rather than inheriting the rotation. */}
      <div className={cn('aspect-[4/5] overflow-hidden rounded-lg bg-neutral-800 shadow-md ring-1 ring-black/10', tiltClassName)}>
        <PreviewImage
          foregroundUrl={thumb.foregroundUrl}
          backgroundUrl={thumb.backgroundUrl}
          containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-cover"
        />
      </div>
      {/* Grade for that year's photo ("2nd Grade" / "3rd Grade"); omitted when the grade
          isn't one we can name (the caller passes null). Upright, centered under the photo. */}
      {thumb.caption && (
        <p className="mt-1.5 text-center text-[11px] md:text-xs font-semibold text-gray-500">{thumb.caption}</p>
      )}
    </div>
  );
}

GrowthThumbnail.defaultProps = { tiltClassName: '', wrapperClassName: '' };

GrowthThumbnail.propTypes = {
  thumb: PropTypes.shape({
    foregroundUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
    caption: PropTypes.string,
  }).isRequired,
  // Rotation applied to the photo only (not the label).
  tiltClassName: PropTypes.string,
  // Layout classes on the outer wrapper (e.g. flex `order` for the mobile reflow).
  wrapperClassName: PropTypes.string,
};

/**
 * One pose card. Split out from the grid so it can detect its own orientation with a hook
 * (hooks can't run inside the .map that renders the grid).
 *
 * Landscape poses — memory mates, "Class of 20XX" graphics, group shots — must NOT be
 * cover-cropped into a portrait frame: that zooms in and slices off the sides (and any
 * baked-in graphic text). The old favorites screen handled this by spanning a landscape
 * pose across two columns (favorite-pose-item's `.big`); we do the same here. A landscape
 * pose gets a landscape (4:3) frame that spans two grid columns; a portrait pose keeps the
 * single-column 3:4 frame. Orientation resolves async from the image, so a pose renders
 * portrait for one frame and re-flows once its dimensions are known — same as the old screen.
 */
function PoseCard({
  pose, backgroundUrl, isYb, isFav, ineligible, step, yearbook,
  onZoom, onSelectYearbook, onToggleFavorite,
}) {
  const { orientation } = useImageOrientation(pose.watermarkUrl);
  const isLandscape = orientation === ORIENTATION.LANDSCAPE;
  const cameraLetter = cameraLetterOf(pose);

  return (
    <div
      className={cn(
        // White matte with a consistent gap between the photo and the frame. The frame IS the
        // selection state: a subtle gray until picked, pink once favorited, blue for the
        // yearbook pose. Ring (not border) so the frame never shifts the layout.
        'relative rounded-2xl bg-white p-1.5 shadow-sm',
        isLandscape && 'col-span-2',
        // eslint-disable-next-line no-nested-ternary
        isYb ? 'ring-2 ring-shoob-blue' : isFav ? 'ring-2 ring-pink-500' : 'ring-1 ring-gray-200',
      )}
    >
      {/* Inner panel holds the photo AND every overlay, so the badges/buttons hug the image
          edge rather than floating in the matte. overflow-hidden + rounded clips to the panel. */}
      <div className={cn('relative overflow-hidden rounded-xl bg-gray-100', isLandscape ? 'aspect-[4/3]' : 'aspect-[3/4]')}>
        {/* PreviewImage layers the background at z-index:-1 and relies on its container being a
            stacking context (position:relative + z-index). Round the images themselves too, so
            a composited child is clipped to the rounded panel on every engine. */}
        <button
          type="button"
          onClick={onZoom}
          aria-label={`Enlarge photo ${poseNumberOf(pose)}`}
          className="absolute inset-0 z-[1] cursor-zoom-in rounded-xl overflow-hidden"
        >
          <PreviewImage
            foregroundUrl={pose.watermarkUrl}
            backgroundUrl={backgroundUrl}
            containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-cover [&_img]:rounded-xl"
          />
        </button>

        {/* Yearbook toggle — blocked, not hidden, on an ineligible pose. */}
        {yearbook && (
          <button
            type="button"
            onClick={onSelectYearbook}
            title={ineligible ? yearbook.note : undefined}
            aria-label={isYb ? 'Yearbook pose' : 'Choose as yearbook pose'}
            className={cn(
              'absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center shadow',
              isYb ? 'bg-shoob-blue text-white' : 'bg-black/60 text-white hover:bg-black/80',
              ineligible && 'opacity-40 cursor-not-allowed',
            )}
          >
            <CapIcon className="h-4 w-4" />
          </button>
        )}

        {/* Frame ID — pose number + camera letter, e.g. "9F". A session is often split
            across cameras, so the pose number alone doesn't identify a frame; this is
            how staff and customers refer to one on the phone. */}
        <span
          className="absolute top-2 left-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white"
          title={`Pose ${poseNumberOf(pose)}${cameraLetter ? `, Camera ${cameraLetter}` : ''}`}
        >
          {`${poseNumberOf(pose)}${cameraLetter}`}
        </span>

        {/* Why the yearbook button is dead on this card. Bottom-left, clear of the frame ID. */}
        {ineligible && step === 1 && (
          <span className="absolute bottom-2 left-2 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
            Not yearbook eligible
          </span>
        )}

        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
          className={cn(
            'absolute bottom-2 right-2 z-10 h-9 w-9 rounded-full flex items-center justify-center shadow',
            isFav ? 'bg-pink-500 text-white' : 'bg-black/40 text-white hover:bg-black/60',
          )}
        >
          <HeartIcon filled={isFav} className="h-4 w-4" />
        </button>

        {isYb && (
          <div className="absolute bottom-0 inset-x-0 z-10 bg-shoob-blue text-white text-center text-sm font-semibold py-1.5 flex items-center justify-center gap-1">
            <CapIcon className="h-4 w-4" />
            {' '}
            YEARBOOK
          </div>
        )}
      </div>
    </div>
  );
}

PoseCard.defaultProps = {
  backgroundUrl: null,
  yearbook: null,
  ineligible: false,
};

PoseCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  pose: PropTypes.object.isRequired,
  backgroundUrl: PropTypes.string,
  isYb: PropTypes.bool.isRequired,
  isFav: PropTypes.bool.isRequired,
  ineligible: PropTypes.bool,
  step: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  yearbook: PropTypes.object,
  onZoom: PropTypes.func.isRequired,
  onSelectYearbook: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
};

/**
 * The photo gallery every audience picks their favorites in (Figma v3).
 *
 * ONE implementation, parameterized — NOT a senior screen with an elementary copy beside
 * it. The audiences differ only in capabilities:
 *
 *   seniors     -> `yearbook` capability supplied (step 1 picks the official yearbook
 *                  pose, garment-gated), plus `backdropUrl` (the school's senior backdrop,
 *                  since seniors are shot as cutouts).
 *   everyone    -> no `yearbook` prop: favorites only, backgrounds per the pricelist's
 *                  package-poses spec. The step banner collapses to a single line.
 *
 * The caller owns what is audience-specific (how a yearbook pose is SAVED, what the
 * backdrop is, the headline); this component owns what is universal (the grid, favorites,
 * ordering, frame IDs, the continue bar). Add a capability prop before you add a copy.
 */
export default function PortraitsGallery({
  poses,
  backgrounds,
  packageId,
  headline,
  subhead,
  leadThumbnail,
  trailThumbnail,
  backdropUrl,
  yearbook,
  favoritesKey,
  promo,
  onContinue,
  continueLabel,
}) {
  const { data: packagePoses } = usePackagePoses(packageId);
  const { data: pricelist } = usePricelist(packageId);

  const [favorites, setFavorites] = useState(() => readFavorites(favoritesKey));

  const yearbookId = yearbook?.selectedPoseId || null;
  // Two-step only when a yearbook pose is being chosen; otherwise it's favorites, one step.
  const step = yearbook && !yearbookId ? 1 : 2;

  // Fail-open: a pose with no verdict is pickable, so a classifier outage never blocks a
  // customer. Without the yearbook capability nothing is ever ineligible.
  const isEligible = (pose) => !yearbook || yearbook.isEligible(pose);

  // Yearbook-eligible poses first, the rest after — a STABLE partition (Array#filter),
  // so within each group the photographer's favorite order (the API's display_order,
  // then number) survives untouched. Ineligible poses are shown, never hidden: this is
  // also the purchase surface, so a cap-and-gown photo stays heartable and buyable —
  // only the yearbook action is gated. Same order in both steps, so nothing reshuffles
  // under the customer's finger once they pick.
  const orderedPoses = useMemo(() => {
    const list = (poses || []).filter(Boolean);
    if (!yearbook) return list;
    // Sort into bands, STABLE within each (the index tiebreaker preserves the API's
    // display_order/number). The senior wrapper supplies a 3-way key (yearbook-formal ->
    // cap & gown -> other); any other audience falls back to eligible-first / rest-after.
    const bandOf = yearbook.bandOf || ((pose) => (yearbook.isEligible(pose) ? 0 : 1));
    return list
      .map((pose, i) => ({ pose, i, band: bandOf(pose) }))
      .sort((a, b) => a.band - b.band || a.i - b.i)
      .map((entry) => entry.pose);
  }, [poses, yearbook]);

  const persistFavorites = (nextSet) => {
    setFavorites(nextSet);
    if (favoritesKey) {
      try {
        window.sessionStorage.setItem(favoritesKey, JSON.stringify([...nextSet]));
      } catch (_) { /* private browsing — favorites just won't survive a reload */ }
    }
  };

  const toggleFavorite = (id) => {
    const next = new Set(favorites);
    if (next.has(id)) next.delete(id); else next.add(id);
    persistFavorites(next);
  };

  const favoriteAll = () => persistFavorites(new Set((poses || []).map((pose) => pose.id)));
  const clearFavorites = () => persistFavorites(new Set());

  // Click-to-enlarge. Parents want a proper look at a photo before hearting it, and the
  // decision they're making here (which photos to buy) is hard to make off a thumbnail.
  const [zoomedPose, setZoomedPose] = useState(null);

  useEffect(() => {
    if (!zoomedPose) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setZoomedPose(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedPose]);

  const selectYearbook = async (pose) => {
    if (!yearbook) return;
    const saved = await yearbook.onSelect(pose);
    if (!saved) return;
    // The yearbook pose is a favorite by definition.
    const next = new Set(favorites);
    next.add(pose.id);
    persistFavorites(next);
  };

  // (The banner no longer shows the yearbook pose number -- the blue-ringed card
  // identifies it; keep only the existence check.)

  // [&_button]:border-0 on the root resets the default browser button border on every button
  // in the gallery. Tailwind's preflight is OFF (to coexist with Bootstrap), so bare <button>s
  // keep their user-agent border — which draws a thin box around each photo card and the icon
  // buttons. One descendant variant clears them all.
  return (
    <div className="mx-auto max-w-6xl px-4 pb-28 [&_button]:border-0">
      {/* Phones: the two photos sit as a touching pair ABOVE a full-width title (flanking a
          140px-wide title breaks it into four lines). Tablet+ : the classic flank, photo /
          title / photo. One flex that reflows via `order` + `basis-full`, so there's no
          second copy of the block (and no Bootstrap-`.hidden` toggle to fight). */}
      <div className="bg-white pt-5 pb-3 sm:py-8 px-4 rounded-b-lg shadow-sm flex flex-wrap sm:flex-nowrap items-center justify-center gap-x-2 gap-y-3 sm:gap-3 md:gap-4">
        {/* Last year — order 1 everywhere (left of the pair on mobile, left flank on desktop). */}
        {leadThumbnail && (
          <GrowthThumbnail thumb={leadThumbnail} tiltClassName="-rotate-6" wrapperClassName="order-1" />
        )}
        {/* Title: full width on mobile so it drops below the pair; inline flank on desktop. */}
        <div className="order-last sm:order-2 basis-full sm:basis-auto text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">{headline}</h1>
          {subhead && (
            <p className="mt-1.5 text-lg md:text-xl font-medium text-gray-500">{subhead}</p>
          )}
        </div>
        {/* This year — beside last year on mobile (order 2), right flank on desktop (order 3). */}
        {trailThumbnail && (
          <GrowthThumbnail thumb={trailThumbnail} tiltClassName="rotate-6" wrapperClassName="order-2 sm:order-3" />
        )}
      </div>

      {/* Step banner. Step 2 is two full-width lines (designer, 07-22): "Yearbook Pose
          Selected" across the top (no pose number -- the blue-ringed card already shows
          which), then the heart + "Select your favorites." as one line. The bulk actions
          (Favorite All / Clear) moved to the sticky bottom bar as real buttons. */}
      <div className="mt-2 bg-shoob-blue text-white rounded-md px-4 py-3">
        {step === 1 && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20">
                <CapIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-bold text-lg leading-tight">Step 1: Select your Yearbook Pose</p>
                <p className="text-sm text-white/90 flex items-center gap-1">
                  Tap the
                  {' '}
                  <CapIcon className="h-4 w-4 inline" />
                  {' '}
                  on the photo you want in the yearbook
                </p>
              </div>
            </div>
            {yearbook && (
              <span className="shrink-0 rounded-full bg-white/20 px-4 py-1 text-sm font-medium">Required</span>
            )}
          </div>
        )}
        {step === 2 && (
          <>
            {yearbook && (
              <div className="flex items-center justify-between gap-3">
                {/* Done, so it recedes -- muted, one line across the box. */}
                <p className="text-base leading-tight text-white/80 flex items-center gap-2 min-w-0">
                  <CapIcon className="h-4 w-4 shrink-0" />
                  Yearbook Pose Selected
                </p>
                <span className="shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-medium flex items-center gap-1">
                  <CapIcon className="h-3.5 w-3.5" />
                  {' '}
                  to change
                </span>
              </div>
            )}
            <p className={cn('text-xl leading-tight font-bold flex items-center gap-2.5', yearbook && 'mt-2')}>
              {/* Pink-circle heart matching the favorite button on each photo card, so the
                  instruction visually points at the control the customer taps. */}
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink-500 shadow">
                <HeartIcon filled className="h-4 w-4 text-white" />
              </span>
              {/* Nudged down a hair so the cap-height text optically centers on the circle. */}
              <span className="relative top-[1px]">Select your favorites.</span>
            </p>
          </>
        )}
      </div>

      {/* Why some photos can't be the yearbook pick — only while one is being chosen. */}
      {step === 1 && yearbook?.note && (
        <p className="mt-3 text-sm text-gray-600">{yearbook.note}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-5">
        {/* Upsell tile (elementary "Download All My Photos"). It leads the grid, as it does
            today — it is a real revenue path, not decoration, so the gallery carries it
            rather than losing it in the redesign. Audiences without one pass no `promo`. */}
        {promo && (
          // On phones the promo is a full-width banner shown at its natural aspect, so none
          // of its baked-in text ("30% OFF", "Download All My Photos") is cover-cropped off
          // the edge. On tablet+ it returns to a single 3:4 card at the head of the grid.
          // NOT one big button: only the BUY NOW strip at the bottom triggers the purchase,
          // so tapping the ad or dragging to scroll past it never adds the package to the cart.
          <div className="col-span-2 sm:col-span-1 relative rounded-2xl overflow-hidden bg-white shadow-sm sm:aspect-[3/4]">
            <img src={promo.imageUrl} alt={promo.label} className="block w-full h-auto sm:h-full sm:object-cover" />
            <button
              type="button"
              onClick={promo.onClick}
              aria-label={promo.label}
              className="absolute inset-x-0 bottom-0 h-[20%] bg-transparent"
            />
          </div>
        )}

        {orderedPoses.map((pose) => (
          <PoseCard
            key={pose.id}
            pose={pose}
            // Caller-supplied backdrop (seniors: the school's senior background) when there
            // is one; otherwise this pose's background per the pricelist's package-poses
            // spec — so a cutout is never rendered bare.
            backgroundUrl={backdropUrl || getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl}
            isYb={!!yearbookId && pose.id === yearbookId}
            isFav={favorites.has(pose.id)}
            ineligible={!isEligible(pose)}
            step={step}
            yearbook={yearbook}
            onZoom={() => setZoomedPose(pose)}
            onSelectYearbook={() => selectYearbook(pose)}
            onToggleFavorite={() => toggleFavorite(pose.id)}
          />
        ))}
      </div>

      {/* Enlarged view. The same composite the card shows, plus the pricelist's graphics
          overlay (getAppropriateForeground) — so what they're judging is what prints.
          Sized to ~1.5x a card (not the whole screen), and clicking anywhere — the photo,
          the backdrop, the X, or Escape — closes it. */}
      {zoomedPose && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${poseNumberOf(zoomedPose)}`}
        >
          {/* Backdrop — its own button so clicking outside the photo closes. bg-transparent
              is load-bearing: preflight is off, so a bare <button> would paint its default
              light-gray buttonface over the dim and wash the backdrop out. */}
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 cursor-zoom-out bg-transparent"
            onClick={() => setZoomedPose(null)}
          />

          <div className="relative z-10 w-[420px] max-w-[90vw]">
            {/* X — always visible in the corner, so closing never depends on scrolling to a
                button below a tall photo (the bug this replaces). */}
            <button
              type="button"
              aria-label="Close"
              onClick={() => setZoomedPose(null)}
              className="absolute -top-3 -right-3 z-20 h-9 w-9 rounded-full bg-white text-gray-800 shadow-lg flex items-center justify-center text-lg leading-none"
            >
              ✕
            </button>

            {/* The photo is itself a close button — clicking it dismisses the viewer. */}
            <button
              type="button"
              aria-label="Close enlarged photo"
              onClick={() => setZoomedPose(null)}
              className="block w-full p-0 cursor-zoom-out"
            >
              <div className="relative overflow-hidden rounded-2xl bg-neutral-900 aspect-[4/5]">
                <PreviewImage
                  foregroundUrl={zoomedPose.watermarkUrl}
                  backgroundUrl={backdropUrl
                    || getAppropriateBg(packagePoses, zoomedPose, backgrounds, pricelist)?.imageUrl}
                  actualForegroundUrl={getAppropriateForeground(packagePoses, zoomedPose, pricelist)?.imageUrl}
                  containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-contain"
                />
              </div>
            </button>

            {/* Frame ID + favorite. Separate from the photo button, so favoriting from the
                zoom doesn't close it. */}
            <div className="mt-3 flex items-center justify-between gap-4">
              <span className="rounded bg-black/60 px-2 py-1 text-sm font-semibold tabular-nums text-white">
                {`${poseNumberOf(zoomedPose)}${cameraLetterOf(zoomedPose)}`}
              </span>

              <button
                type="button"
                onClick={() => toggleFavorite(zoomedPose.id)}
                className={cn(
                  'flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold shadow',
                  favorites.has(zoomedPose.id)
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-900 hover:bg-gray-100',
                )}
              >
                <HeartIcon filled={favorites.has(zoomedPose.id)} className="h-4 w-4" />
                {favorites.has(zoomedPose.id) ? 'Favorited' : 'Favorite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky bar: Favorite All + the continue CTA, both Shoob blue. On a phone they
          stack full-width; on desktop they sit at the right, where the "Download all my
          photos" promo already anchors the left of the grid. padding-bottom clears the
          home indicator. */}
      {step === 2 && (
        <div
          className="fixed bottom-0 inset-x-0 z-20 bg-white border-t border-gray-200 px-4 pt-3"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto max-w-6xl flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
            {/* Shoob blue (#008fcf) per the designer -- replaces the gold Submit
                styling. "Favorite All" shares the row and reuses the same
                favoriteAll as the header link; it disables once everything is
                already a favorite. The CTA label still carries its disabled reason. */}
            <button
              type="button"
              onClick={favoriteAll}
              disabled={poses.length > 0 && favorites.size === poses.length}
              className={cn(
                'w-full sm:w-auto rounded-full px-6 py-3 font-semibold text-white bg-shoob-blue flex items-center justify-center gap-2 shadow',
                poses.length > 0 && favorites.size === poses.length
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-shoob-blueDark',
              )}
            >
              Favorite All
            </button>
            <button
              type="button"
              onClick={clearFavorites}
              disabled={favorites.size === 0}
              className={cn(
                'w-full sm:w-auto rounded-full px-6 py-3 font-semibold text-white bg-shoob-blue flex items-center justify-center gap-2 shadow',
                favorites.size === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-shoob-blueDark',
              )}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => onContinue([...favorites])}
              disabled={favorites.size === 0}
              className={cn(
                'w-full sm:w-auto rounded-full px-6 py-3 font-semibold text-white bg-shoob-blue flex items-center justify-center gap-2 shadow',
                favorites.size === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-shoob-blueDark',
              )}
            >
              {favorites.size === 0 ? 'Pick your favorites' : continueLabel}
              {favorites.size > 0 && <span aria-hidden="true">→</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PortraitsGallery.defaultProps = {
  poses: [],
  backgrounds: [],
  subhead: null,
  leadThumbnail: null,
  trailThumbnail: null,
  backdropUrl: null,
  yearbook: null,
  favoritesKey: null,
  promo: null,
  onContinue: () => {},
  continueLabel: 'Build Your Package',
};

PortraitsGallery.propTypes = {
  // Spraypaint Pose models — objects, not a fixed shape.
  // eslint-disable-next-line react/forbid-prop-types
  poses: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: PropTypes.arrayOf(PropTypes.object),
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  headline: PropTypes.string.isRequired,
  // Optional tagline under the headline (e.g. a season/theme line like "Growing Up").
  subhead: PropTypes.string,
  // "Growing Up" flanking portraits: last year (left, tilted) and this year (right). Each is
  // { foregroundUrl (watermark cutout), backgroundUrl (backdrop to composite on) }.
  leadThumbnail: PropTypes.shape({
    foregroundUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
    caption: PropTypes.string,
  }),
  trailThumbnail: PropTypes.shape({
    foregroundUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
    caption: PropTypes.string,
  }),
  // Backdrop for every card, overriding the per-pose package background. Seniors are shot
  // as cutouts and printed on the school's senior background, so they pass it; audiences
  // whose backgrounds come from the pricelist pass nothing.
  backdropUrl: PropTypes.string,
  // The yearbook-pick capability. Omit it and the gallery is favorites-only: no cap
  // buttons, no eligibility gate, no step 1.
  yearbook: PropTypes.shape({
    selectedPoseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // (pose) => bool. Fail-open on an unclassified pose.
    isEligible: PropTypes.func.isRequired,
    // (pose) => 0|1|2. Optional 3-way ordering band; falls back to isEligible when absent.
    bandOf: PropTypes.func,
    // (pose) => Promise<bool>. Persists the choice; resolve false and nothing is favorited.
    onSelect: PropTypes.func.isRequired,
    // Shown while choosing, and as the blocked button's tooltip.
    note: PropTypes.string,
  }),
  // sessionStorage key for the favorites, so they survive into the package builder.
  favoritesKey: PropTypes.string,
  // An upsell tile at the head of the grid (elementary "Download All My Photos").
  promo: PropTypes.shape({
    imageUrl: PropTypes.string.isRequired,
    label: PropTypes.string,
    onClick: PropTypes.func.isRequired,
  }),
  onContinue: PropTypes.func,
  continueLabel: PropTypes.string,
};
